// Feedback types, enums, and interfaces (FEEDBACK_FEATURE_PLAN.md §5).
// Single source of truth — imported by both api and web.

import type { PtSheetScanFeedbackContext } from "./physicalTherapy.js";
import type { ProactiveSuggestionDebug } from "./proactiveSuggestions.js";
import type { ExerciseRequestFeedbackContext } from "./exerciseLookup.js";
import type { BackfillDebug, MissedDoseBackfillFeedbackContext } from "./medicationBackfill.js";

export type FeedbackRating = "up" | "down";

export const FEEDBACK_CATEGORIES = [
  { id: "inaccurate",    label: "Inaccurate / wrong info" },
  { id: "unhelpful",     label: "Unhelpful / didn't address it" },
  { id: "wrong_capture", label: "Captured the wrong event" },
  { id: "missing",       label: "Missed / didn't capture something" },
  { id: "tone",          label: "Tone / wording" },
  { id: "other",         label: "Other" },
] as const;

export type FeedbackCategory = (typeof FEEDBACK_CATEGORIES)[number]["id"];

export const FEEDBACK_STATUSES = ["new", "reviewing", "fix_deployed", "verified", "resolved"] as const;
export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number];

/** Per-turn Bedrock usage supplied by the client for live-session ratings (D-FB-7). */
export interface FeedbackUsageTurn {
  seq: number;
  modelId: string;
  inputTokens: number;
  outputTokens: number;
  durationMs?: number;
  costUsd: number;
}

export interface FeedbackUsageTotals {
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  turns: number;
}

/** Snapshot blob stored in S3 (FEEDBACK_FEATURE_PLAN.md §3.2). Version 1. */
export interface FeedbackSnapshot {
  version: 1;
  capturedAt: string;
  feedbackId: string;
  patientId: string;
  userId: string;
  mode: "capture" | "onboarding";
  sessionId: string;
  sessionTitle: string;
  rating: FeedbackRating;
  reasonCategory?: FeedbackCategory;
  reasonText?: string;
  /** Which UI control the rating was submitted from (e.g. "chat_footer",
   *  "suggestion_card") — helps triage tell a stale/context-free rating from one
   *  that carries a full pendingProposal, without guessing from category/text. */
  sourceSurface?: string;
  build: { appVersion: string };
  models: { capture?: string; onboarding?: string };
  patient: Record<string, unknown>;
  regimens: unknown[];
  recentEvents: unknown[];
  transcript: Array<{ seq: number; role: string; content: string; createdAt: string }>;
  pendingProposal?: unknown;
  /** Server-computed snapshot of the proactive medication-suggestion engine's
   *  live state for this patient at rating time (mode="capture", rating="down"
   *  only). Independent of whatever the client sent as `pendingProposal` — this
   *  is recomputed fresh from the patient's regimens + recent medication history,
   *  so it's captured even when the user rated from a surface (like the Chat
   *  page's general feedback footer) that has no visibility into the Dashboard's
   *  suggestion card state. See PROACTIVE_SUGGESTIONS_PLAN.md. */
  suggestionDebug?: ProactiveSuggestionDebug;
  /** Server-computed snapshot of the missed-dose backfill sweep's live state
   *  (sourceSurface === "missed_dose_backfill", rating === "down" only). Fresh
   *  recompute of buildBackfillSlots() plus the recent medication history it read
   *  from — lets a reviewer verify a specific slot's fill math (computeDayFills) independent
   *  of whatever the client-attached pendingProposal shows. */
  backfillDebug?: BackfillDebug;
  /** null for historical-session ratings (D-FB-7) */
  usage: {
    turns: FeedbackUsageTurn[];
    totals: FeedbackUsageTotals;
  } | null;
  settings: Record<string, unknown>;
}

/** Context captured when rating the Condition report modal's Timeline tab
 *  (feedback for the auto-close span-merging behavior — reports that look
 *  "continuous" when a symptom actually had gaps, D-COND). */
export interface ConditionReportFeedbackContext {
  kind: "condition_report";
  conditionId: string;
  conditionName: string;
  tab: "calendar" | "timeline" | "stats";
  rangeDays: number;
  autoCloseDays: number;
  symptoms: Array<{
    key: string;
    label: string;
    /** Literal contiguous-logged-day spans as currently rendered by ConditionTimeline. */
    occurrenceSpans: Array<{ startDate: string; endDate: string; peakSeverity: number }>;
    /** Carried-forward "active" spans (Calendar/Stats tabs), for comparison. */
    activitySpans: Array<{ startDate: string; endDate: string; peakSeverity: number }>;
    /** Raw per-day severities backing the carried-forward spans. */
    severityByDay: Array<{ date: string; severity: number }>;
    /** True if the user flagged this symptom's row as wrong in a down-rating. */
    flagged?: boolean;
  }>;
}

/** Context captured when rating the per-item report modal (Calendar/Chart/
 *  Stats tabs) opened from a Logs row or a Condition report drill-down. */
export interface ItemReportFeedbackContext {
  kind: "item_report";
  itemKey: string;
  itemLabel: string;
  eventType: string;
  tab: "calendar" | "chart" | "stats";
  rangeDays: number;
  autoCloseDays: number;
  events: Array<{ eventId: string; occurredAt: string; payload: unknown }>;
}

/** How many days of exercise history a Log-tab recommendation rating carries.
 *  Wider than the 7-day window the strip itself is computed on, so a review
 *  can see the sessions just outside that window. */
export const LOG_RECOMMENDATION_LOOKBACK_DAYS = 14;

/** Context captured when rating the Trainer Log tab's Recommended strip.
 *  Passed as `pendingProposal` on the existing /feedback route. */
export interface LogRecommendationFeedbackContext {
  kind: "log_recommendations";
  ratedAt: string;
  timezone: string;
  logLookbackDays: typeof LOG_RECOMMENDATION_LOOKBACK_DAYS;
  /** Days computeBalanceRecommendations actually used. */
  windowDays: number;
  asOfIso: string;
  targetSessionsPerWeek: number;
  includeSpecialistMuscles: boolean;
  coldStart: boolean;
  recoveryDay: boolean;
  filters: {
    ownerScope: "all" | "mine";
    /** "all" or a gym label. */
    gym: string;
    search: string;
  };
  /** Items on the Recommended strip, in display order, including the sentence under each tile. */
  recommended: Array<{
    id: string;
    source: "own" | "catalog";
    name: string;
    reason?: string;
    kind: "session" | "recovery-mobility" | "recovery-light";
    score: number;
    coveredMuscles: string[];
    dueMuscles: string[];
    restingMuscles: string[];
    catalogReason?: "inventory-gap" | "variety";
    sameDay: boolean;
    alternatives: string[];
    flagged?: boolean;
  }>;
  ownNotShown: Array<{
    id: string;
    name: string;
    reason: "no-muscle-tags" | "no-gap" | "recovering" | "covered-by-pick";
    readyInHours?: number;
  }>;
  deficits: Array<{ muscle: string; common: string; deficit: number }>;
  dueMuscles: string[];
  deferredMuscles: string[];
  /** Muscles that had load, a deficit, or a recovery clock — the ranker's inputs. */
  muscleState: Array<{
    muscle: string;
    common: string;
    effectiveTrainingDays: number;
    lastResistanceAt?: string;
    recoveryState: "no-data" | "recovering" | "ready";
    fractionRecovered: number;
    recoveryHours: number;
  }>;
  /** Owned equipment eligible after the on-screen filters. */
  eligibleEquipment: Array<{
    id: string;
    name: string;
    gymLabel?: string;
    purpose?: string;
    primaryMuscles: string[];
    secondaryMuscles: string[];
  }>;
  /** gym_exercise events in the lookback, newest first. */
  recentLogs: Array<{
    eventId: string;
    occurredAt: string;
    name: string;
    userExerciseId: string;
    sets?: number;
    reps?: number;
    weight?: number;
    weightUnit?: string;
    durationMin?: number;
    distanceMi?: number;
    distanceUnit?: string;
    side?: string;
    primaryMuscles: string[];
    secondaryMuscles: string[];
    gymLabel?: string;
    notes?: string;
  }>;
  /** PT sessions folded into the same muscle math, within the lookback. */
  recentPt: Array<{
    occurredAt: string;
    name: string;
    primaryMuscles: string[];
    secondaryMuscles: string[];
  }>;
}

/** Context captured when rating the Trainer Log tab's per-exercise log modal
 *  (the form opened by tapping an exercise tile). Passed as `pendingProposal`
 *  on the existing /feedback route. Identifies WHICH exercise the rating is for,
 *  plus what the form held, so a review needs no guesswork. */
export interface GymExerciseLogFeedbackContext {
  kind: "gym_exercise_log";
  ratedAt: string;
  timezone: string;
  /** True when editing a previously logged set rather than logging a new one. */
  editing: boolean;
  /** True when the form was prefilled from Today's workout. */
  fromWorkoutPrefill: boolean;
  exercise: {
    userExerciseId: string;
    name: string;
    catalogId?: string;
    equipmentType: string;
    brand?: string;
    gymLabel?: string;
    ownerPatientId: string;
    isOwn: boolean;
    ownerLabel?: string;
    unilateral: boolean;
    metrics: string[];
    primaryMuscles: string[];
    secondaryMuscles: string[];
    primaryMuscleIds?: string[];
    secondaryMuscleIds?: string[];
    photoCount: number;
    lastLoggedAt?: string;
    logCount?: number;
    /** Values the viewer's last log left behind (what the form prefills from). */
    lastValues?: Record<string, unknown>;
  };
  /** What the form showed at the moment of rating (unsaved edits included). */
  form: {
    values: Record<string, string>;
    weightUnit?: string;
    distanceUnit?: string;
    side?: string;
    notes?: string;
    timeMode: string;
  };
  /** The logged event being edited, when `editing`. */
  editingEvent?: { eventId: string; occurredAt: string; payload: unknown };
  /** Aspects the user ticked on a thumbs-down. */
  flagged?: string[];
}

const CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  FEEDBACK_CATEGORIES.map((c) => [c.id, c.label])
);

/** Human label for a category id (falls back to the raw id). */
export function feedbackCategoryLabel(id?: string): string {
  return (id && CATEGORY_LABELS[id]) || id || "—";
}

/** DynamoDB feedback item (FEEDBACK_FEATURE_PLAN.md §3.1). */
export interface FeedbackItem {
  feedbackId: string;
  patientId: string;
  patientFirstName: string;
  userId: string;
  mode: "capture" | "onboarding";
  sessionId: string;
  sessionTitle: string;
  rating: FeedbackRating;
  reasonCategory?: FeedbackCategory;
  reasonText?: string;
  /** Only for down ratings (D-FB-6). */
  status?: FeedbackStatus;
  messageCount: number;
  lastSeq: number;
  modelIds?: Record<string, string>;
  appVersion: string;
  snapshotS3Key: string;
  createdAt: string;
  updatedAt: string;
}

// ---- De-identification (D-FB-9) ----

/** Escape a string for safe use inside a RegExp. */
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Collect candidate human names from the snapshot's patient block so they can be
 *  replaced everywhere (transcript, reason, profile summary). Clinical content
 *  (meds, doses, symptoms, times) is intentionally left untouched. */
function collectNames(snap: FeedbackSnapshot): Array<{ from: string; to: string }> {
  const patient = snap.patient ?? {};
  const out: Array<{ from: string; to: string }> = [];
  const first = typeof patient.firstName === "string" ? patient.firstName.trim() : "";
  const last = typeof patient.lastName === "string" ? patient.lastName.trim() : "";
  if (first) out.push({ from: first, to: "[PATIENT]" });
  if (last) out.push({ from: last, to: "[LAST]" });
  if (first && last) out.unshift({ from: `${first} ${last}`, to: "[PATIENT]" });
  // Longer strings first so multi-word names replace before their parts.
  return out.sort((a, b) => b.from.length - a.from.length);
}

function scrubString(s: string, names: Array<{ from: string; to: string }>): string {
  let out = s;
  for (const { from, to } of names) {
    out = out.replace(new RegExp(`\\b${escapeRegExp(from)}\\b`, "gi"), to);
  }
  return out;
}

/** Pure transform: replace patient/relative/provider names with placeholders,
 *  keeping all clinical content. Returns a new snapshot; does not mutate input. */
export function deidentifySnapshot(snap: FeedbackSnapshot): FeedbackSnapshot {
  const names = collectNames(snap);
  const scrub = (s: string) => scrubString(s, names);

  const patient: Record<string, unknown> = { ...snap.patient };
  if (patient.firstName) patient.firstName = "[PATIENT]";
  if (patient.lastName) patient.lastName = "[LAST]";
  if (patient.dob) patient.dob = "[REDACTED]";
  if (typeof patient.profileSummary === "string") patient.profileSummary = scrub(patient.profileSummary);

  return {
    ...snap,
    reasonText: snap.reasonText ? scrub(snap.reasonText) : snap.reasonText,
    sessionTitle: scrub(snap.sessionTitle),
    patient,
    transcript: snap.transcript.map((m) => ({ ...m, content: scrub(m.content) })),
  };
}

// ---- Improvement prompt (§5) ----

function fmtUsd(n: number): string {
  return `$${n.toFixed(4)}`;
}

/** Render the paste-ready improvement prompt from a snapshot (FEEDBACK_FEATURE_PLAN.md §5). */
export function buildImprovementPrompt(
  snap: FeedbackSnapshot,
  opts?: { deidentify?: boolean }
): string {
  const s = opts?.deidentify ? deidentifySnapshot(snap) : snap;
  const lines: string[] = [];

  const kind =
    s.pendingProposal != null && typeof s.pendingProposal === "object" && "kind" in s.pendingProposal
      ? String((s.pendingProposal as Record<string, unknown>).kind)
      : undefined;
  const isPtSheetScanFeedback = kind === "pt_sheet_scan";
  const isConditionReportFeedback = kind === "condition_report";
  const isItemReportFeedback = kind === "item_report";
  const isExerciseRequestFeedback = kind === "exercise_request";
  const isBackfillFeedback = kind === "missed_dose_backfill";
  const isLogRecommendationFeedback = kind === "log_recommendations";
  const isGymLogFeedback = kind === "gym_exercise_log";
  const isSuggestionFeedback =
    !isPtSheetScanFeedback && !isConditionReportFeedback && !isItemReportFeedback &&
    !isExerciseRequestFeedback && !isBackfillFeedback && !isLogRecommendationFeedback &&
    !isGymLogFeedback &&
    kind != null && kind.includes("medication");
  const isReportFeedback = isConditionReportFeedback || isItemReportFeedback;

  lines.push(
    isExerciseRequestFeedback
      ? "A user asked for an exercise to be ADDED to the curated exercise library. This is a " +
          "feature request, not a complaint about a wrong answer. Below is what they searched for, " +
          "what they described, and what the describe-lookup returned. Add the requested exercise to " +
          "the curated catalog in shared/src/gymCatalog.ts, following the 'HOW TO ADD A NEW CATALOG " +
          "ENTRY' checklist in that file's header: a unique kebab-case id, aliases covering the " +
          "vernacular and manufacturer names, coarse primaryMuscles/secondaryMuscles, the matching " +
          "primaryMuscleIds/secondaryMuscleIds from shared/src/muscleMap.ts (every id's parent group " +
          "must appear in the coarse array), the metrics that make sense to log, a generic " +
          "GENERIC_GYM_ICONS iconRef, and a one-to-two sentence description. Then run " +
          "`npm run validate:muscles` — it must exit 0."
      : isPtSheetScanFeedback
      ? "You are improving a medical-logging assistant. A user gave a thumbs-" + s.rating +
          " on the review screen after scanning a printed PT exercise sheet (OCR + AI " +
          "extraction of exercises, including a cropped illustration image per exercise). " +
          "Below is the full context captured at rating time. Diagnose what went wrong " +
          "(bad OCR/extraction of an exercise's name or instructions, a missed exercise, or " +
          "an incorrectly cropped illustration) and propose specific changes — most likely to " +
          "the extraction prompt in api/src/ptSheetIngest.ts or the crop logic in " +
          "web/src/lib/image.ts (cropImageToDataUrl / CROP_PADDING_FRAC)."
      : isConditionReportFeedback
      ? "You are improving a medical-logging assistant. A user gave a thumbs-" + s.rating +
          " on the Condition report modal's " + (s.pendingProposal as ConditionReportFeedbackContext).tab +
          " tab. Below is the full context captured at rating time, including per-symptom " +
          "merged spans (as rendered) and the raw per-day severities they were built from. " +
          "Diagnose what looks wrong (e.g. spans merging across gaps that should be shown as " +
          "separate episodes) and propose specific changes — most likely to buildSymptomActivity " +
          "in shared/src/symptomActivity.ts or the rendering in web/src/components/report/ConditionTimeline.tsx."
      : isItemReportFeedback
      ? "You are improving a medical-logging assistant. A user gave a thumbs-" + s.rating +
          " on the per-item report modal's " + (s.pendingProposal as ItemReportFeedbackContext).tab +
          " tab (web/src/components/ItemReportModal.tsx). Below is the full context captured at " +
          "rating time, including every raw event backing the displayed chart/calendar/stats. " +
          "Diagnose what looks wrong and propose specific changes."
      : isLogRecommendationFeedback
      ? "You are improving a medical-logging assistant. A user gave a thumbs-" + s.rating +
          " on the Recommended strip of the Trainer Log tab (web/src/components/TrainerPane.tsx). " +
          "That strip is deterministic: computeBalanceRecommendations in shared/src/muscleMap.ts, " +
          "fed by computeTrainingLoad in shared/src/trainingLoad.ts. It is not the Balance-tab AI " +
          "call and not Today's workout. Below is the list that was on screen, the muscle-load " +
          "inputs that produced it, and the last " +
          (s.pendingProposal as LogRecommendationFeedbackContext).logLookbackDays +
          " days of exercise logs (wider than the " +
          (s.pendingProposal as LogRecommendationFeedbackContext).windowDays +
          "-day window the ranker used). The ranker does not read pain, injury, soreness, sleep, " +
          "time of day, or the active program — if the complaint is about one of those, the fix " +
          "is to add that input, not only to retune the deficit math."
      : isGymLogFeedback
      ? "You are improving a medical-logging assistant. A user gave a thumbs-" + s.rating +
          " on the exercise log modal of the Trainer Log tab (GymLogModal in " +
          "web/src/components/TrainerPane.tsx) — the form for logging one set/session of a single " +
          "piece of equipment. Below is WHICH exercise it was (its definition, muscle tags, metrics, " +
          "gym, and the values it prefilled from), what the form held when they rated, and their " +
          "comment. Diagnose what is wrong with this exercise or this form (bad muscle tags, wrong " +
          "metrics for the equipment, a wrong or stale prefill, the dosing guidance, a confusing " +
          "control) and propose specific changes. If the problem is in the exercise definition, " +
          "say so rather than changing shared logic."
      : isBackfillFeedback
      ? "You are improving a medical-logging assistant. A user gave a thumbs-" + s.rating +
          " on the Missed Doses catch-up review list (web/src/components/MissedDoseBackfill.tsx), " +
          "which is populated by the backfill sweep (api/src/lib/backfill.ts buildBackfillSlots / " +
          "computeDayFills). Below is the full context captured at rating time, including the on-screen " +
          "slot list and staged decisions, plus a live recompute of the sweep and the recent " +
          "medication history it read from. Diagnose why a slot was wrongly flagged as missed (or " +
          "a genuinely missed dose failed to appear) and propose specific logic changes to the " +
          "sweep — most likely to computeDayFills(), everyNDaysOnDay(), or the learned-cluster averaging " +
          "in backfill.ts."
      : isSuggestionFeedback
      ? "You are improving a medical-logging assistant. A user gave a thumbs-down to a " +
          "proactive medication suggestion. Below is the full context captured at rating time. " +
          "The suggestion object is in the 'On-screen pending proposal' section. Diagnose why " +
          "the wrong medications were suggested and propose specific logic or configuration changes " +
          "to the suggestion algorithm to fix it."
      : "You are improving a medical-logging assistant. A user gave a thumbs-down to a " +
          "conversation. Below is the full context captured at rating time. Diagnose what " +
          "went wrong and propose specific prompt and/or code changes to fix it."
  );
  lines.push("");

  lines.push("## User's rating");
  lines.push(`- Rating: ${s.rating}`);
  if (s.reasonCategory) lines.push(`- Category: ${feedbackCategoryLabel(s.reasonCategory)}`);
  if (s.reasonText) lines.push(`- Reason: ${s.reasonText}`);
  lines.push(
    isExerciseRequestFeedback
      ? "- Flow: Exercise library request"
      : isPtSheetScanFeedback
      ? "- Flow: PT sheet scan review"
      : isReportFeedback
      ? `- Flow: ${isConditionReportFeedback ? "Condition report" : "Item report"} modal`
      : isBackfillFeedback
      ? "- Flow: Missed Doses catch-up review"
      : isLogRecommendationFeedback
      ? "- Flow: Trainer Log recommendations"
      : isGymLogFeedback
      ? "- Flow: Trainer exercise log modal"
      : `- Conversation mode: ${s.mode}`
  );
  if (s.sourceSurface) lines.push(`- Submitted from: ${s.sourceSurface}`);
  lines.push("");

  if (isExerciseRequestFeedback) {
    const ctx = s.pendingProposal as ExerciseRequestFeedbackContext;
    lines.push("## Exercise request");
    lines.push(`- Searched for: ${ctx.searchTerm}`);
    if (ctx.description) lines.push(`- Described as: ${ctx.description}`);
    lines.push(`- Surface: ${ctx.surface}`);
    for (const c of ctx.lookupResult?.candidates ?? []) {
      lines.push(`- ${c.name} (catalogId: ${c.catalogId ?? "none"}, ${c.confidence}) — ${c.reason}`);
    }
    lines.push("");
  }

  // Guardrail: a capture-mode rating with no transcript and no client-supplied
  // pendingProposal usually means the user rated from a surface (e.g. the Chat
  // page's general feedback footer) that had no visibility into whatever they
  // were actually reacting to (e.g. a Dashboard suggestion card). suggestionDebug
  // (below, when present) is a best-effort recovery — a fresh recompute of the
  // suggestion engine's state — but it reflects "now", not necessarily the exact
  // moment/settings the user saw. Flag this so the diagnosis isn't overstated.
  if (s.mode === "capture" && s.transcript.length === 0 && !s.pendingProposal) {
    lines.push("## Note");
    lines.push(
      "This feedback has no chat transcript and no client-attached pendingProposal — " +
        "it was very likely submitted via the general conversation feedback control " +
        "rather than a control tied to the specific thing being complained about " +
        "(e.g. a proactive suggestion card). " +
        (s.suggestionDebug
          ? "A live recompute of the medication-suggestion engine's state is included " +
            "below as `## Live medication-suggestion engine state`, but it reflects the " +
            "engine's state at the time this snapshot was built, not necessarily the exact " +
            "moment or on-screen state the user was reacting to."
          : "No suggestion-engine recompute is attached (mode/rating didn't qualify, or the " +
            "patient/regimens failed to load) — treat any root-cause diagnosis below as " +
            "speculative and prefer asking the user to reproduce it via the specific " +
            "control tied to the thing they're complaining about.")
    );
    lines.push("");
  }

  lines.push("## Build / model");
  lines.push(`- App version: ${s.build?.appVersion ?? "unknown"}`);
  if (s.models?.capture) lines.push(`- Capture model: ${s.models.capture}`);
  if (s.models?.onboarding) lines.push(`- Onboarding model: ${s.models.onboarding}`);
  lines.push("");

  lines.push("## Patient profile");
  const p = s.patient ?? {};
  if (typeof p.profileSummary === "string" && p.profileSummary.trim()) {
    lines.push(p.profileSummary.trim());
  } else {
    for (const [k, v] of Object.entries(p)) {
      if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) continue;
      lines.push(`- ${k}: ${Array.isArray(v) ? v.join(", ") : String(v)}`);
    }
  }
  lines.push("");

  lines.push("## Active medications / regimens");
  if (s.regimens.length === 0) {
    lines.push("(none)");
  } else {
    for (const r of s.regimens as Array<Record<string, unknown>>) {
      const name = r.name ?? r.drugName ?? r.medication ?? "medication";
      const dose = r.dose ?? r.dosage ?? "";
      const freq = r.frequency ?? r.schedule ?? "";
      lines.push(`- ${name}${dose ? ` ${dose}` : ""}${freq ? ` — ${freq}` : ""}`);
    }
  }
  lines.push("");

  lines.push("## Recent log (last 7 days)");
  if (s.recentEvents.length === 0) {
    lines.push("(none)");
  } else {
    for (const e of s.recentEvents as Array<Record<string, unknown>>) {
      const when = e.occurredAt ?? e.createdAt ?? "";
      const type = e.eventType ?? e.type ?? "event";
      lines.push(`- ${when} ${type}: ${JSON.stringify(e)}`);
    }
  }
  lines.push("");

  lines.push("## Transcript");
  for (const m of s.transcript) {
    lines.push(`[${m.seq}] ${m.role}: ${m.content}`);
  }
  lines.push("");

  if (s.suggestionDebug) {
    const d = s.suggestionDebug;
    lines.push("## Live medication-suggestion engine state (computed at feedback time)");
    lines.push(
      `- Now: ${d.now} (${d.nowLocalTime} ${d.timezone})`
    );
    lines.push(
      `- Window mode: ${d.settings.windowMode} (global -${d.globalWindow.beforeMin}/+${d.globalWindow.afterMin} min)`
    );
    if (d.inactiveMedNames.length) {
      lines.push(`- Inactive/paused meds (never suggested): ${d.inactiveMedNames.join(", ")}`);
    }
    for (const note of d.notes) lines.push(`- Note: ${note}`);
    lines.push("");

    const included = d.candidates.filter((c) => c.included);
    const excluded = d.candidates.filter((c) => !c.included);

    lines.push(
      `### Currently WOULD be suggested (${included.length})` +
        (included.length === 0 ? " — none" : "")
    );
    for (const c of included) {
      lines.push(`- ${c.name} (${c.source}, group "${c.group}"): ${c.reasons.join(" ")}`);
    }
    lines.push("");

    lines.push(`### Currently excluded (${excluded.length})`);
    for (const c of excluded) {
      lines.push(`- ${c.name} (${c.source}, group "${c.group}"): ${c.reasons.join(" ")}`);
      if (c.recentDoses.length) {
        const doses = c.recentDoses
          .slice(0, 5)
          .map((rd) => `${rd.localTime}${rd.missed ? " [missed]" : ""}${rd.dose ? ` (${rd.dose})` : ""}`)
          .join("; ");
        lines.push(`  Recent doses: ${doses}`);
      }
    }
    lines.push("");
  }

  if (s.backfillDebug) {
    const d = s.backfillDebug;
    lines.push("## Live missed-dose backfill sweep state (computed at feedback time)");
    lines.push(
      "Caveat: this is a fresh recompute, so it reflects the sweep's state when this snapshot " +
        "was built — not necessarily the exact on-screen list the user reacted to. Where an " +
        "`## On-screen missed-dose list` section is also present, that one is what they saw."
    );
    lines.push(`- Now: ${d.now} (${d.timezone})`);
    lines.push(
      `- Fill rule: a day's expected anchors are matched to that day's logged doses BY COUNT — ` +
        `each anchor claims the nearest unclaimed dose within ±${d.toleranceHours}h, anchors within ` +
        `±${d.toleranceHours}h of an already-claimed dose still count as filled, then leftover ` +
        `doses logged anywhere on the anchor's local day fill the remaining anchors. So N logged ` +
        `doses can only ever account for N anchors.`
    );
    lines.push(`- Lookback: ${d.lookbackDays} days`);
    lines.push(`- Slots the sweep currently reports as missed (${d.slots.length}):`);
    for (const slot of d.slots) {
      lines.push(
        `  - ${slot.name}${slot.strength ? ` (${slot.strength})` : ""} — expected ${slot.dayKey} ` +
          `${slot.anchorLocalTime} (source: ${slot.source})`
      );
    }
    lines.push(`- Recent medication_taken history read by the sweep (${d.recentDoses.length}):`);
    for (const rd of d.recentDoses.slice(0, 40)) {
      lines.push(`  - ${rd.occurredAt} ${rd.name}${rd.missed ? " [missed]" : ""}`);
    }
    lines.push("");
  }

  if (isPtSheetScanFeedback) {
    const ctx = s.pendingProposal as PtSheetScanFeedbackContext;
    lines.push("## Scanned PT sheet under review");
    lines.push(`- Job ID: ${ctx.jobId}`);
    if (ctx.sheetTitle) lines.push(`- Program title: ${ctx.sheetTitle}`);
    if (ctx.cadence) lines.push(`- Cadence: ${ctx.cadence}`);
    lines.push(`- Pages uploaded: ${ctx.pageCount}`);
    lines.push(`- Exercises extracted: ${ctx.exercises.length}`);
    lines.push("");
    for (const ex of ctx.exercises) {
      const flag = ex.flagged ? " [FLAGGED BY USER]" : "";
      const removedTag = ex.removed ? " (user removed this row)" : "";
      lines.push(`### ${ex.name}${flag}${removedTag}`);
      lines.push(ex.description);
      if (ex.imageArea) {
        lines.push(
          `- Crop box (page ${ex.imagePageIndex ?? 0}): x=${ex.imageArea.x.toFixed(3)}, ` +
            `y=${ex.imageArea.y.toFixed(3)}, w=${ex.imageArea.w.toFixed(3)}, h=${ex.imageArea.h.toFixed(3)}`
        );
      } else {
        lines.push("- No illustration crop box was extracted for this exercise.");
      }
      lines.push("");
    }
  } else if (isConditionReportFeedback) {
    const ctx = s.pendingProposal as ConditionReportFeedbackContext;
    lines.push("## Condition report under review");
    lines.push(`- Condition: ${ctx.conditionName} (${ctx.conditionId})`);
    lines.push(`- Tab: ${ctx.tab}`);
    lines.push(`- Range: ${ctx.rangeDays} days`);
    lines.push(`- Auto-close horizon: ${ctx.autoCloseDays} days`);
    lines.push("");
    for (const sym of ctx.symptoms) {
      const flag = sym.flagged ? " [FLAGGED BY USER]" : "";
      lines.push(`### ${sym.label}${flag}`);
      lines.push(`- Occurrence spans (as rendered): ${JSON.stringify(sym.occurrenceSpans)}`);
      lines.push(`- Carried-forward activity spans: ${JSON.stringify(sym.activitySpans)}`);
      lines.push(`- Raw per-day severities: ${JSON.stringify(sym.severityByDay)}`);
      lines.push("");
    }
  } else if (isItemReportFeedback) {
    const ctx = s.pendingProposal as ItemReportFeedbackContext;
    lines.push("## Item report under review");
    lines.push(`- Item: ${ctx.itemLabel} (${ctx.itemKey}, ${ctx.eventType})`);
    lines.push(`- Tab: ${ctx.tab}`);
    lines.push(`- Range: ${ctx.rangeDays} days`);
    lines.push(`- Auto-close horizon: ${ctx.autoCloseDays} days`);
    lines.push(`- Raw events (${ctx.events.length}):`);
    lines.push("```json");
    lines.push(JSON.stringify(ctx.events, null, 2));
    lines.push("```");
    lines.push("");
  } else if (isBackfillFeedback) {
    const ctx = s.pendingProposal as MissedDoseBackfillFeedbackContext;
    lines.push("## On-screen missed-dose list (as shown to the user)");
    lines.push(`- Slots shown (${ctx.slots.length}):`);
    for (const slot of ctx.slots) {
      const decision = ctx.staged.find((st) => st.slotId === slot.slotId);
      const decisionLabel = decision
        ? decision.missed
          ? "staged: Didn't take"
          : `staged: Took it at ${decision.takenAt}`
        : "no decision staged yet";
      lines.push(
        `  - ${slot.name}${slot.strength ? ` (${slot.strength})` : ""} — expected ${slot.dayKey} ` +
          `${slot.anchorLocalTime} (source: ${slot.source}) — ${decisionLabel}`
      );
    }
    if (ctx.added?.length) {
      lines.push(`- Medications the user added that were not on the list (${ctx.added.length}):`);
      for (const a of ctx.added) lines.push(`  - ${a.name} — staged: Took it at ${a.takenAt}`);
    }
    lines.push("");
  } else if (isGymLogFeedback) {
    const ctx = s.pendingProposal as GymExerciseLogFeedbackContext;
    const ex = ctx.exercise;
    lines.push("## Exercise being logged (the thing rated)");
    lines.push(`- Name: ${ex.name} (${ex.userExerciseId})`);
    lines.push(
      `- Equipment type: ${ex.equipmentType}` +
        (ex.catalogId ? `; catalog id: ${ex.catalogId}` : "; custom (no catalog id)") +
        (ex.brand ? `; brand: ${ex.brand}` : "") +
        (ex.gymLabel ? `; gym: ${ex.gymLabel}` : "")
    );
    lines.push(
      `- Owner: ${ex.isOwn ? "the rating user" : `another household member${ex.ownerLabel ? ` (${ex.ownerLabel})` : ""}`}` +
        ` [${ex.ownerPatientId}]; unilateral: ${ex.unilateral}; photos: ${ex.photoCount}`
    );
    lines.push(`- Metrics logged: ${ex.metrics.join(", ") || "(none)"}`);
    lines.push(
      `- Primary muscles: ${ex.primaryMuscles.join(", ") || "(none)"}` +
        (ex.primaryMuscleIds?.length ? ` [${ex.primaryMuscleIds.join(", ")}]` : "")
    );
    lines.push(
      `- Secondary muscles: ${ex.secondaryMuscles.join(", ") || "(none)"}` +
        (ex.secondaryMuscleIds?.length ? ` [${ex.secondaryMuscleIds.join(", ")}]` : "")
    );
    if (ex.logCount != null || ex.lastLoggedAt) {
      lines.push(`- Viewer history: logged ${ex.logCount ?? "?"} times, last ${ex.lastLoggedAt ?? "never"}`);
    }
    if (ex.lastValues) lines.push(`- Last-values prefill source: ${JSON.stringify(ex.lastValues)}`);
    lines.push("");
    lines.push("## Form state when rated");
    lines.push(
      `- Mode: ${ctx.editing ? "editing an existing log" : "new log"}` +
        (ctx.fromWorkoutPrefill ? " (prefilled from Today's workout)" : "")
    );
    lines.push(`- Values: ${JSON.stringify(ctx.form.values)}`);
    if (ctx.form.weightUnit) lines.push(`- Weight unit: ${ctx.form.weightUnit}`);
    if (ctx.form.distanceUnit) lines.push(`- Distance unit: ${ctx.form.distanceUnit}`);
    if (ctx.form.side) lines.push(`- Side: ${ctx.form.side}`);
    if (ctx.form.notes) lines.push(`- Notes: ${ctx.form.notes}`);
    lines.push(`- Time mode: ${ctx.form.timeMode}`);
    if (ctx.editingEvent) {
      lines.push(`- Event being edited (${ctx.editingEvent.eventId}, ${ctx.editingEvent.occurredAt}):`);
      lines.push("```json");
      lines.push(JSON.stringify(ctx.editingEvent.payload, null, 2));
      lines.push("```");
    }
    if (ctx.flagged?.length) lines.push(`- User flagged: ${ctx.flagged.join(", ")}`);
    lines.push(`- Rated at ${ctx.ratedAt} (${ctx.timezone})`);
    lines.push("");
  } else if (isLogRecommendationFeedback) {
    const ctx = s.pendingProposal as LogRecommendationFeedbackContext;
    lines.push("## Recommendations on screen");
    lines.push(
      `- Window: last ${ctx.windowDays} days ending ${ctx.asOfIso} (${ctx.timezone}), ` +
        `target ${ctx.targetSessionsPerWeek} sessions/week per muscle`
    );
    lines.push(
      `- Cold start: ${ctx.coldStart}; recovery day: ${ctx.recoveryDay}; ` +
        `specialist muscles: ${ctx.includeSpecialistMuscles}`
    );
    lines.push(
      `- Filters: owner=${ctx.filters.ownerScope}, gym=${ctx.filters.gym}` +
        (ctx.filters.search ? `, search="${ctx.filters.search}"` : "")
    );
    if (ctx.recommended.length === 0) {
      lines.push("- Nothing was listed in the Recommended strip.");
    }
    for (const item of ctx.recommended) {
      const flag = item.flagged ? " [FLAGGED BY USER]" : "";
      lines.push(
        `- ${item.name} (${item.source}, ${item.kind}, score ${item.score})${flag}` +
          (item.reason ? ` — ${item.reason}` : "")
      );
    }
    if (ctx.ownNotShown.length > 0) {
      lines.push("- Owned equipment not shown:");
      for (const row of ctx.ownNotShown) {
        lines.push(
          `  - ${row.name}: ${row.reason}` +
            (row.readyInHours != null ? ` (ready in ~${row.readyInHours}h)` : "")
        );
      }
    }
    lines.push("");
    lines.push("## Muscle state the ranker used");
    for (const m of ctx.muscleState) {
      lines.push(
        `- ${m.common} (${m.muscle}): ${m.effectiveTrainingDays} effective days, ` +
          `recovery ${m.recoveryState}` +
          (m.lastResistanceAt ? `, last resistance ${m.lastResistanceAt}` : "")
      );
    }
    lines.push("");
    lines.push(`## Exercise logs (last ${ctx.logLookbackDays} days, ${ctx.recentLogs.length})`);
    for (const log of ctx.recentLogs) {
      const bits = [
        log.sets != null ? `${log.sets} sets` : "",
        log.reps != null ? `${log.reps} reps` : "",
        log.weight != null ? `${log.weight} ${log.weightUnit ?? ""}`.trim() : "",
        log.durationMin != null ? `${log.durationMin} min` : "",
        log.side ? log.side : "",
      ].filter(Boolean);
      lines.push(
        `- ${log.occurredAt} ${log.name}` +
          (bits.length ? ` (${bits.join(", ")})` : "") +
          (log.notes ? ` — ${log.notes}` : "")
      );
    }
    if (ctx.recentPt.length > 0) {
      lines.push(`## PT sessions counted in the same math (${ctx.recentPt.length})`);
      for (const pt of ctx.recentPt) {
        lines.push(`- ${pt.occurredAt} ${pt.name}`);
      }
    }
    lines.push("");
  } else if (s.pendingProposal) {
    lines.push(
      isSuggestionFeedback
        ? "## Proactive medication suggestion (rated by user)"
        : "## On-screen pending proposal (not yet saved)"
    );
    lines.push("```json");
    lines.push(JSON.stringify(s.pendingProposal, null, 2));
    lines.push("```");
    lines.push("");
  }

  if (s.usage) {
    lines.push("## Per-turn usage (live session)");
    for (const t of s.usage.turns) {
      lines.push(
        `- turn ${t.seq} (${t.modelId}): in ${t.inputTokens} / out ${t.outputTokens} tok` +
          `${t.durationMs != null ? `, ${t.durationMs}ms` : ""}, ${fmtUsd(t.costUsd)}`
      );
    }
    lines.push(
      `- totals: ${s.usage.totals.turns} turns, in ${s.usage.totals.inputTokens} / ` +
        `out ${s.usage.totals.outputTokens} tok, ${fmtUsd(s.usage.totals.costUsd)}`
    );
    lines.push("");
  }

  lines.push("## Task");
  lines.push(
    isLogRecommendationFeedback
      ? "Identify why this Recommended strip was wrong for the logs and settings above, and " +
          "propose a specific change to computeBalanceRecommendations or computeTrainingLoad. " +
          "Cite the muscle, the log, and the rule that produced the bad pick."
      : "Identify the root cause of the thumbs-down and propose specific prompt/code changes " +
          "to prevent it. Be concrete: cite the turn(s) at fault and give exact wording or logic fixes."
  );

  return lines.join("\n");
}
